/**
 * The ONE map from a backend error to operator-facing copy (R10c H2-4).
 *
 * The server answers every failure with `{error: {code, message, details}}`. `message` is English
 * and, for a 422, can be a validator/regex string ("status: String should match pattern ...") -
 * neither is fit for a Hindi-first city desk. So the desk never renders `message`: it keys off the
 * typed `code` (PR-1's `web/errors.py` codes) and falls back to the HTTP status class, and every
 * outcome is a translated `operator.errors.*` string (EN + HI in the dictionaries).
 *
 * Self-contained on purpose (no `@/lib/api` import): panels share it under test mocks that replace
 * that module wholesale.
 *
 * Codes: these are the backend `ErrorCode` values PR-1 actually emits. There is no `city_required`
 * or `invalid_status` code - PR-1 scopes an out-of-city operator with `403 permission_denied` and
 * reports a bad status as a `422 validation_failed` whose `details.fields[]` carry the per-field
 * `validation.*` codes (see `docs/ERROR_CODES.md`). `field_invalid` below is a client-only synthetic
 * `classify` returns for a 422 that does carry a field list; it is not a backend code.
 */
export const OPERATOR_ERROR_KEYS = {
  field_invalid: "operator.errors.checkFields",
  validation_failed: "operator.errors.checkFields",
  unauthenticated: "operator.errors.signedOut",
  permission_denied: "operator.errors.forbidden",
  not_found: "operator.errors.notFound",
  conflict: "operator.errors.conflict",
  too_many_requests: "operator.errors.tooMany",
  upgrade_required: "operator.errors.upgrade",
  maintenance: "operator.errors.maintenance",
  method_not_allowed: "operator.errors.methodNotAllowed",
  bad_request: "operator.errors.badRequest",
  internal: "operator.errors.server",
  network: "operator.errors.network",
  timeout: "operator.errors.timeout",
  generic: "operator.errors.generic",
};

const STATUS_CODE = {
  400: "bad_request",
  401: "unauthenticated",
  403: "permission_denied",
  404: "not_found",
  405: "method_not_allowed",
  409: "conflict",
  422: "validation_failed",
  426: "upgrade_required",
  429: "too_many_requests",
  503: "maintenance",
};

/** The error's code key (one of OPERATOR_ERROR_KEYS), never a server string. */
export function classify(error) {
  if (!error) return "generic";
  const response = error.response;
  // A request that hit its timeout (axios `ECONNABORTED`, or `useDeskLoad`'s own timer) has no
  // response either, but "check your connection and retry" is a different line from "offline".
  if (!response) return error.code === "ECONNABORTED" ? "timeout" : "network";
  const code = response.data?.error?.code;
  if (typeof code === "string" && OPERATOR_ERROR_KEYS[code]) return code;
  // Until PR-1's specific codes land, the "Choose a city" 422 carries no field list.
  if (response.status === 422) {
    const fields = response.data?.error?.details?.fields;
    return Array.isArray(fields) && fields.length > 0 ? "field_invalid" : "validation_failed";
  }
  if (STATUS_CODE[response.status]) return STATUS_CODE[response.status];
  if (response.status >= 500) return "internal";
  return "generic";
}

/** Translated copy for a failed request. `t` is the app's translate function. */
export function operatorErrorMessage(error, t) {
  return t(OPERATOR_ERROR_KEYS[classify(error)]);
}
